import React, { useMemo } from 'react'
import { PropTypes } from 'prop-types'

import usePool from '@duckness/use-pool'
import InsightsDashboardPool from '@aytm/app/insights/dashboard/duckness/InsightsDashboardPool'

import InlineSelector from '../InlineSelector'

import ColorComplex from '@aytm/lib/rfd-ui/components/advanced/color-complex'
import CheckBox from '@aytm/lib/rfd-ui/components/basic/check-box'
import { ReassignSigTestLetters } from '@aytm/app/insights/dashboard/components/ui/ReassignSigTestLetters'

import { stateSelector, shouldUpdate } from './selectors'

import './Footer.scss'

const CLASS = 'insights-portal-footer'

const LEVELS = [0.999, 0.99, 0.98, 0.95, 0.9, 0.8]

const Footer = props => {
  const { isSharedView, showSigtest, confidenceLevel, secondaryConfidenceLevel, blockColors } = usePool(
    InsightsDashboardPool,
    stateSelector,
    shouldUpdate
  )

  const colors = useMemo(() => blockColors?.toJS() || null, [blockColors])

  const className = props.className || CLASS

  const confidenceLevelOptions = useMemo(
    () =>
      LEVELS.filter(v => v >= secondaryConfidenceLevel).map(v => {
        return {
          code: v,
          name: `${v * 100}%`
        }
      }),
    [secondaryConfidenceLevel]
  )

  const secondaryConfidenceLevelOptions = useMemo(
    () =>
      LEVELS.filter(v => v <= confidenceLevel).map(v => {
        return {
          code: v,
          name: `${v * 100}%`
        }
      }),
    [confidenceLevel]
  )

  const onShowSigtestChange = event => {
    if (props.onElementToggle) {
      const newShowSigtest = event
      if (newShowSigtest !== showSigtest) {
        props.onElementToggle({ showSigtest: newShowSigtest })
      }
    }
  }

  const onSelectConfidenceLevel = event => {
    if (props.onConfidenceLevelChange) {
      const newConfidenceLevel = event.value
      if (newConfidenceLevel !== confidenceLevel) {
        props.onConfidenceLevelChange({ confidenceLevel: newConfidenceLevel })
      }
    }
  }

  const onSelectSecondaryConfidenceLevel = event => {
    if (props.onSecondaryConfidenceLevelChange) {
      const newSecondaryConfidenceLevel = event.value
      if (newSecondaryConfidenceLevel !== secondaryConfidenceLevel) {
        props.onSecondaryConfidenceLevelChange({ secondaryConfidenceLevel: newSecondaryConfidenceLevel })
      }
    }
  }

  const onSelectColor = event => {
    if (props.onColorsChange) props.onColorsChange({ colors: event.colors })
  }

  const style = props.blockWidth ? { width: `${props.blockWidth}px` } : null

  return (
    <div className={className} style={style}>
      <CheckBox
        className={'insights-portal'}
        label={'Show significance testing'}
        checked={showSigtest}
        disabled={isSharedView}
        onChange={onShowSigtestChange}
      />
      <ReassignSigTestLetters disabled={isSharedView || !showSigtest || !props.hasData} />
      <InlineSelector
        confirmKeyboardSelect
        className={`${className}-level`}
        label={'Confidence level:'}
        value={`${confidenceLevel * 100}%`}
        options={confidenceLevelOptions}
        disabled={isSharedView || !showSigtest}
        onChange={onSelectConfidenceLevel}
      />
      <InlineSelector
        confirmKeyboardSelect
        className={`${className}-level`}
        label={'Secondary confidence level:'}
        value={`${secondaryConfidenceLevel * 100}%`}
        options={secondaryConfidenceLevelOptions}
        disabled={isSharedView || !showSigtest}
        onChange={onSelectSecondaryConfidenceLevel}
      />
      <ColorComplex
        className={`${className}-color`}
        label={'Cell colors:'}
        name={'complex'}
        colors={colors}
        disabled={isSharedView || !showSigtest}
        onSelect={onSelectColor}
      />
    </div>
  )
}

Footer.propTypes = {
  hasData: PropTypes.bool,
  className: PropTypes.string,
  blockWidth: PropTypes.number,
  onConfidenceLevelChange: PropTypes.func,
  onSecondaryConfidenceLevelChange: PropTypes.func,
  onColorsChange: PropTypes.func,
  onElementToggle: PropTypes.func
}

export default Footer
